import * as THREE from "three";
import type { EntityLike } from "./types";

export type CustomRenderer = (entity: EntityLike, mesh: THREE.Object3D | null) => THREE.Object3D | null;

/**
 * Generic palette renderer per SPEC.md §4. Holds a Map<entityId, Object3D>,
 * synchronizes transforms and materials, and dispatches non-palette kinds
 * to an optional custom renderer (set by per-room client modules).
 */
export class EntityRenderer {
  private readonly scene: THREE.Scene;
  private readonly meshes = new Map<string, THREE.Object3D>();
  private customRenderer: CustomRenderer | null = null;
  private readonly textureLoader = new THREE.TextureLoader();

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  setCustomRenderer(fn: CustomRenderer | null): void {
    this.customRenderer = fn;
  }

  addEntity(entity: EntityLike): void {
    if (this.meshes.has(entity.id)) {
      this.updateEntity(entity);
      return;
    }
    const obj = this.createObject(entity, null);
    if (!obj) return;
    obj.name = `entity:${entity.id}`;
    this.applyTransform(obj, entity);
    this.scene.add(obj);
    this.meshes.set(entity.id, obj);
  }

  updateEntity(entity: EntityLike): void {
    const existing = this.meshes.get(entity.id);
    if (!existing) {
      this.addEntity(entity);
      return;
    }
    // For palette kinds, mutate in place. For custom kinds, give the
    // custom renderer a chance to replace the object (rare but allowed).
    const replacement = this.createObject(entity, existing);
    if (replacement && replacement !== existing) {
      this.scene.remove(existing);
      this.disposeObject(existing);
      replacement.name = `entity:${entity.id}`;
      this.applyTransform(replacement, entity);
      this.scene.add(replacement);
      this.meshes.set(entity.id, replacement);
      return;
    }
    this.applyTransform(existing, entity);
    this.applyColor(existing, entity.color);
  }

  removeEntity(id: string): void {
    const obj = this.meshes.get(id);
    if (!obj) return;
    this.scene.remove(obj);
    this.disposeObject(obj);
    this.meshes.delete(id);
  }

  getMesh(id: string): THREE.Object3D | undefined {
    return this.meshes.get(id);
  }

  clear(): void {
    for (const [id] of this.meshes) this.removeEntity(id);
  }

  private createObject(entity: EntityLike, existing: THREE.Object3D | null): THREE.Object3D | null {
    // Try custom renderer first (matches non-palette kinds).
    if (this.customRenderer && !PALETTE.has(entity.kind)) {
      try {
        const out = this.customRenderer(entity, existing);
        if (out) return out;
      } catch (err) {
        console.warn(`[renderer] custom renderer threw for kind=${entity.kind}:`, err);
      }
    }
    if (existing && PALETTE.has(entity.kind)) {
      // existing object is fine; signal "keep current" by returning the same ref
      return existing;
    }
    return this.createPaletteObject(entity);
  }

  private createPaletteObject(entity: EntityLike): THREE.Object3D {
    switch (entity.kind) {
      case "cube": {
        const geom = new THREE.BoxGeometry(1, 1, 1);
        const mat = new THREE.MeshStandardMaterial({ color: parseColor(entity.color) });
        return new THREE.Mesh(geom, mat);
      }
      case "sphere": {
        const geom = new THREE.SphereGeometry(0.5, 16, 16);
        const mat = new THREE.MeshStandardMaterial({ color: parseColor(entity.color) });
        return new THREE.Mesh(geom, mat);
      }
      case "plane": {
        const geom = new THREE.PlaneGeometry(1, 1);
        const mat = new THREE.MeshStandardMaterial({
          color: parseColor(entity.color),
          side: THREE.DoubleSide,
        });
        const mesh = new THREE.Mesh(geom, mat);
        const facing = entity.props.get("facing");
        if (facing === "up") mesh.rotation.x = -Math.PI / 2;
        else if (facing === "down") mesh.rotation.x = Math.PI / 2;
        else if (facing === "left") mesh.rotation.y = Math.PI / 2;
        else if (facing === "right") mesh.rotation.y = -Math.PI / 2;
        else if (facing === "back") mesh.rotation.y = Math.PI;
        return mesh;
      }
      case "cylinder": {
        const geom = new THREE.CylinderGeometry(0.5, 0.5, 1, 16);
        const mat = new THREE.MeshStandardMaterial({ color: parseColor(entity.color) });
        return new THREE.Mesh(geom, mat);
      }
      case "light-point": {
        const intensity = parseNumber(entity.props.get("intensity"), 1);
        const distance = parseNumber(entity.props.get("distance"), 0);
        return new THREE.PointLight(parseColor(entity.color), intensity, distance);
      }
      case "light-directional": {
        const intensity = parseNumber(entity.props.get("intensity"), 1);
        return new THREE.DirectionalLight(parseColor(entity.color), intensity);
      }
      case "text": {
        const text = entity.props.get("text") ?? entity.id;
        const fontSize = parseNumber(entity.props.get("fontSize"), 32);
        return makeTextSprite(text, fontSize, entity.color);
      }
      case "sound": {
        // PositionalAudio needs an AudioListener wired through ClientContext.
        // For now create an empty Object3D placeholder; the per-room client
        // module is expected to swap in a real audio node via the custom
        // renderer hook. Logged so the gap is visible.
        console.warn(`[renderer] sound kind not yet supported in palette: ${entity.id}`);
        return new THREE.Object3D();
      }
      default: {
        // Fallback: magenta error cube.
        console.warn(`[renderer] unknown kind="${entity.kind}" for entity ${entity.id} — magenta fallback`);
        const geom = new THREE.BoxGeometry(1, 1, 1);
        const mat = new THREE.MeshBasicMaterial({ color: 0xff00ff });
        return new THREE.Mesh(geom, mat);
      }
    }
  }

  private applyTransform(obj: THREE.Object3D, entity: EntityLike): void {
    obj.position.set(entity.position.x, entity.position.y, entity.position.z);
    obj.rotation.set(entity.rotation.x, entity.rotation.y, entity.rotation.z);
    // Default scale: 0/0/0 from a fresh Vec3 is meaningless; treat as 1,1,1.
    const sx = entity.scale.x === 0 ? 1 : entity.scale.x;
    const sy = entity.scale.y === 0 ? 1 : entity.scale.y;
    const sz = entity.scale.z === 0 ? 1 : entity.scale.z;
    obj.scale.set(sx, sy, sz);
  }

  private applyColor(obj: THREE.Object3D, colorStr: string): void {
    const color = parseColor(colorStr);
    if (obj instanceof THREE.Mesh) {
      const mat = obj.material;
      if (Array.isArray(mat)) {
        for (const m of mat) if ("color" in m) (m as THREE.MeshStandardMaterial).color.set(color);
      } else if (mat && "color" in mat) {
        (mat as THREE.MeshStandardMaterial).color.set(color);
      }
    } else if (obj instanceof THREE.Light) {
      obj.color.set(color);
    }
  }

  private disposeObject(obj: THREE.Object3D): void {
    obj.traverse((node) => {
      if (node instanceof THREE.Mesh) {
        node.geometry?.dispose();
        const mat = node.material;
        if (Array.isArray(mat)) for (const m of mat) m.dispose();
        else mat?.dispose();
      } else if (node instanceof THREE.Sprite) {
        node.material?.map?.dispose();
        node.material?.dispose();
      }
    });
  }
}

const PALETTE = new Set<string>([
  "cube",
  "sphere",
  "plane",
  "cylinder",
  "light-point",
  "light-directional",
  "text",
  "sound",
]);

function parseColor(value: string): THREE.ColorRepresentation {
  if (!value) return 0xffffff;
  try {
    return new THREE.Color(value);
  } catch {
    return 0xffffff;
  }
}

function parseNumber(value: string | undefined, fallback: number): number {
  if (value === undefined) return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function makeTextSprite(text: string, fontSize: number, color: string): THREE.Sprite {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return new THREE.Sprite(new THREE.SpriteMaterial({ color: parseColor(color) }));
  }
  ctx.font = `${fontSize}px sans-serif`;
  const metrics = ctx.measureText(text);
  const padding = 8;
  canvas.width = Math.max(2, Math.ceil(metrics.width + padding * 2));
  canvas.height = Math.max(2, Math.ceil(fontSize * 1.4 + padding * 2));
  ctx.font = `${fontSize}px sans-serif`;
  ctx.textBaseline = "top";
  ctx.fillStyle = color || "#ffffff";
  ctx.fillText(text, padding, padding);
  const tex = new THREE.CanvasTexture(canvas);
  tex.needsUpdate = true;
  const mat = new THREE.SpriteMaterial({ map: tex, transparent: true });
  const sprite = new THREE.Sprite(mat);
  // 1 world-unit per ~64 canvas px feels reasonable; per-entity scale overrides.
  sprite.scale.set(canvas.width / 64, canvas.height / 64, 1);
  return sprite;
}
